import type { GridApi } from 'ag-grid-community';
import type { z } from 'zod';

import type {
  BankFeedTransactionRow,
  BankFeedTransactionsExportBaseSchema,
} from 'domain/bankFeed/schemas';
import { isServiceColumn } from 'components/AgGrid/helpers';

// Subset of the BE export body produced from the current grid view. Field
// names match the schema directly so the consumer can spread without rename.
type BankFeedGridSnapshot = Pick<
  z.infer<typeof BankFeedTransactionsExportBaseSchema>,
  'filter_model' | 'sort_model' | 'headers'
>;

// AG Grid mixes data columns with FE-only service pseudo-columns (column
// visibility menu etc.) — BE rejects unknown column ids with 422 "Invalid
// request to banking service". Strip via the project's canonical
// `isServiceColumn` allowlist before sending.
export const buildGridSnapshot = (
  api: GridApi<BankFeedTransactionRow>,
): BankFeedGridSnapshot => {
  const columnState = api.getColumnState().filter((c) => !isServiceColumn(c.colId));
  const sort_model = columnState
    .filter((c): c is typeof c & { sort: 'asc' | 'desc' } => c.sort === 'asc' || c.sort === 'desc')
    .map((c) => ({ colId: c.colId, sort: c.sort }));
  const headers = columnState.filter((c) => !c.hide).map((c) => c.colId);
  return {
    filter_model: api.getFilterModel() ?? {},
    sort_model,
    headers,
  };
};
